import { APIError } from "../core/errors";

const API_URL_ORIGIN = process.env.REACT_APP_API_URL_ORIGIN;

// リフレッシュトークン中のフラグとリクエストキュー
let isRefreshing = false;
let failedQueue = [];

// キューに溜まったリクエストを処理
const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// リフレッシュトークンを使って新しいアクセストークンを取得
const refreshAccessToken = async () => {
  const response = await fetch(API_URL_ORIGIN + "/api/auth/refresh/", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    mode: "cors",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Refresh failed");
  }

  return response.json();
};

export const postApi = async (url, data) => {
  const ret = await fetchApi(url, "POST", data);
  return ret;
};

export const getApi = async (url) => {
  const ret = await fetchApi(url, "GET");
  return ret;
};

export const fetchApi = async (
  url,
  method,
  requestBody = null,
  isRetry = false
) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const fullUrl = API_URL_ORIGIN + url;

  try {
    const body = requestBody ? JSON.stringify(requestBody) : null;
    const res = await fetch(fullUrl, {
      method,
      headers,
      body,
      mode: "cors", //クロスオリジンリクエストをするのでCORSモードにする
      credentials: "include", //クレデンシャルを含める指定
    });

    // 401エラーの場合、リフレッシュを試みる
    if (res.status === 401 && !isRetry && !url.includes("/api/auth/refresh/")) {
      if (isRefreshing) {
        // すでにリフレッシュ中の場合はキューに追加して待機
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return fetchApi(url, method, requestBody, true);
        });
      }

      isRefreshing = true;

      try {
        // リフレッシュトークンを使って新しいアクセストークンを取得
        await refreshAccessToken();

        // リフレッシュ成功
        processQueue(null);
        isRefreshing = false;

        // 元のリクエストを再実行
        return fetchApi(url, method, requestBody, true);
      } catch (refreshError) {
        // リフレッシュ失敗
        processQueue(refreshError);
        isRefreshing = false;

        throw new APIError("API Error", { status: 401 });
      }
    }

    if (!res.ok) {
      throw new APIError("API Error", { status: res.status });
    }

    const json = await res.json();
    return json;
  } catch (error) {
    console.log("There was an error", error);
    if (error instanceof APIError) {
      // APIErrorはそのまま投げる
      throw error;
    }
    throw new APIError("API Error", { status: error.status || 500 });
  }
};

/**
 * APIエラー時の共通処理（管理者）
 */
export const getApiErrorAdmin = (e, urlPath, param) => {
  //アラート表示後の遷移先
  let location = "/admin/apierror";
  if (e instanceof APIError) {
    //API内容をコンソール出力
    console.error("APIエラー : " + e.status + e.message);
    console.error("url : " + urlPath);
    console.log("パラメータ : " + param);

    // 認証失敗
    switch (e.status) {
      case 401:
        // ログイン画面に遷移
        location = "/admin/login?sessionexp=1";
        break;

      case 404:
        // バックエンドからの404コードはシステムエラー画面に遷移
        break;

      default:
        // システムエラー画面に遷移
        break;
    }

    document.location = location;
  }
};

/**
 * APIエラー時の共通処理（管理者）
 */
export const postApiErrorAdmin = (e, urlPath = "", postData = "") => {
  let location = "/admin/apierror";
  if (e instanceof APIError) {
    //Post内容をコンソール出力
    console.error("APIエラー : " + e.status + e.message);
    console.error("url : " + urlPath);
    console.log("POSTデータ : " + postData);

    //TODO 排他チェック >>「他のユーザーが更新していますとか？」
    // 認証失敗
    switch (e.status) {
      case 400: //シリアライザー
        // システムエラー画面に遷移
        break;

      case 401: //セッションタイムアウト
        // ログイン画面に遷移
        location = "/admin/login?sessionexp=1";
        break;

      case 404:
        // バックエンドからの404コードはシステムエラー画面に遷移
        break;

      case 422: //TODO 表示する文言を確認
        // システムエラー画面に遷移
        break;

      default:
        // システムエラー画面に遷移
        break;
    }

    document.location = location;
  }
};

/**
 * getAPIエラー時の共通処理（マイページ）
 */
export const getApiErrorMypage = (e, urlPath, param = "") => {
  //アラート表示後の遷移先
  let location = "/mypage/apierror";
  if (!localStorage.userData) {
    location = "/error";
  }
  if (e instanceof APIError) {
    //API内容をコンソール出力
    console.error("APIエラー : " + e.status + e.message);
    console.error("url : " + urlPath);
    console.log("パラメータ : " + param);

    // 認証失敗
    switch (e.status) {
      case 401:
        location = "/mypage/login?sessionexp=1";
        // 念のためログアウトと同じ処理をする
        localStorage.removeItem("userData");
        break;

      case 403:
        location = "/mypage/apierror?forbidden=1";
        if (localStorage.userData) {
          let userdata = JSON.parse(localStorage.userData);
          if (userdata.is_admin) {
            // 管理者権限ありの場合はログイン画面へ
            location = "/mypage/login";
          }
        }
        break;

      case 404:
        location = "/mypage/notfound";
        break;

      default:
        break;
    }
    document.location = location;
  }
};

/**
 * postAPIエラー時の共通処理（マイページ）
 */
export const postApiErrorMypage = (e, urlPath = "", postData = "") => {
  let location = "/mypage/apierror";
  if (!localStorage.userData) {
    location = "/error";
  }
  if (e instanceof APIError) {
    //Post内容をコンソール出力
    console.error("APIエラー : " + e.status + e.message);
    console.error("url : " + urlPath);
    console.log("POSTデータ : " + postData);

    // 認証失敗
    switch (e.status) {
      case 401: //セッションタイムアウト
        location = "/mypage/login?sessionexp=1";
        // 念のためログアウトと同じ処理をする
        localStorage.removeItem("userData");
        break;

      case 403:
        location = "/mypage/apierror?forbidden=1";
        if (localStorage.userData) {
          let userdata = JSON.parse(localStorage.userData);
          if (userdata.is_admin) {
            // 管理者権限ありの場合はログイン画面へ
            location = "/mypage/login";
          }
        }
        break;

      case 404:
        location = "/mypage/notfound";
        break;

      default:
        break;
    }

    document.location = location;
  }
};
